// Single loader for the Google Maps JavaScript API.
//
// Maps must be loaded exactly once per page. The bootstrap script at
// maps.googleapis.com/maps/api/js asynchronously injects further scripts of its
// own (main, common, and one per library), and those reference window.google
// when they run. Deleting window.google or removing the bootstrap tag leaves
// those in-flight scripts to fail, and because they are cross-origin the browser
// reports only "Script error." with no message or stack.
//
// Every consumer therefore shares one load, with the union of the libraries any
// of them needs. Requesting different library sets from different components is
// what previously forced a reload: a component wanting `drawing` would find
// window.google without it, delete it, and inject a second bootstrap.
const LIBRARIES = ["drawing", "geometry"];

let loadPromise = null;

function isReady() {
  return LIBRARIES.every((lib) => window.google?.maps?.[lib]);
}

export function loadGoogleMaps(apiKey) {
  if (isReady()) return Promise.resolve(window.google);
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve, reject) => {
    const settle = (script) => {
      script.addEventListener("load", () => resolve(window.google));
      script.addEventListener("error", () => {
        // Allow a later mount to retry rather than caching the failure forever.
        loadPromise = null;
        reject(new Error("Failed to load the Google Maps JavaScript API"));
      });
    };

    // Survives hot reloads, which re-run module scope but leave the DOM intact.
    const existing = document.querySelector("script[data-google-maps]");
    if (existing) {
      settle(existing);
      return;
    }

    const script = document.createElement("script");
    script.src =
      `https://maps.googleapis.com/maps/api/js?key=${apiKey}` +
      `&libraries=${LIBRARIES.join(",")}`;
    script.async = true;
    script.defer = true;
    script.dataset.googleMaps = "true";
    settle(script);
    document.head.appendChild(script);
  });

  return loadPromise;
}

export function getGoogleMapsApiKey() {
  return (
    window.__ENV__?.GOOGLE_MAPS_API_KEY || process.env.REACT_APP_GOOGLE_MAPS_API_KEY
  );
}

